<script setup lang="ts">
/**
 * Main payments table tab.
 *
 * Infinite-scrolls `/payments` and renders each record as a `PaymentRow` in
 * `DynamicTable`. The status column uses `EnumCell` with a three-state icon
 * map (`IDENTIFIED` → green check, `AMBIGUOUS` / `UNIDENTIFIED` → amber
 * warning triangle). Row click opens `PaymentCard`; create/edit is handled
 * through `useAddPaymentFormStore`.
 *
 * Both desktop and mobile group rows under sticky date headers the same way
 * `ServicesTab` does: `groupedData` splices a synthetic `date-header` row
 * ahead of each date's first payment, and `pickRowOverride` renders it as a
 * full-width sticky divider through `DateHeaderRowCell` (shared with
 * `ServicesTab`). Since the date is shown once in the header, desktop's
 * `Time` column and mobile's `PaymentMobileRightCell` each show only the
 * payment's time instead of repeating the date per row.
 */
import { computed, markRaw, ref, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import AddButton from '@/components/table/AddButton.vue'
import CompactAddButton from '@/components/table/CompactAddButton.vue'
import NameCell from '@/components/table/cell/NameCell.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import EnumCell from '@/components/table/cell/EnumCell.vue'
import PaymentMobileLeftCell from '@/components/table/cell/PaymentMobileLeftCell.vue'
import PaymentMobileRightCell from '@/components/table/cell/PaymentMobileRightCell.vue'
import DateHeaderRowCell from '@/components/table/cell/DateHeaderRowCell.vue'
import PaymentCardMobileScreen from '@/components/main/paymentCard/PaymentCardMobileScreen.vue'
import { useIsVisible } from '@/composables/useIsVisible'
import type { EnumCellIconSpec } from '@/components/table/cell/EnumCell.vue'
import SubscriptionTagCell from '@/components/table/cell/SubscriptionTagCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import { deletePayment, fetchPayments, paymentAmountMinorUnits, type Payment } from '@/api/payments'
import { formatName } from '@/utils/formatName'
import { moneyColumnLabel } from '@/utils/formatMoney'
import { formatDateTime } from '@/utils/formatDateTime'
import { paymentsPollingInterval } from '@/config/api'
import { usePaymentCardStore } from '@/stores/payment_card_store'
import { useAddPaymentFormStore } from '@/stores/add_payment_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { useSubscriptionTypeCatalog } from '@/composables/useSubscriptionTypeCatalog'
import { ResourceAccessRole } from '@/models/resource_access_role'

const paymentCardStore = usePaymentCardStore()
const addPaymentFormStore = useAddPaymentFormStore()
const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()
const { types: subscriptionTypes } = useSubscriptionTypeCatalog()

const mobileScrollSentinel = ref<HTMLElement | null>(null)
const mobileScrollSentinelVisible = useIsVisible(mobileScrollSentinel)
const isMobileScrolled = computed(() => isMobile.value && !mobileScrollSentinelVisible.value)

interface PaymentRow {
  // Row for the payments table, mapped from GET `/payments`.

  kind: 'payment'
  // Discriminant against `PaymentDateHeaderRow` in `groupedData`'s union, so
  // `pickRowOverride` and the mobile column `props()` functions can narrow.

  id: string
  // Payment id (UUID7); powers row selection and the detail card endpoint.

  rowNumber: number
  // 1-based index over all loaded pages (same as Clients tab).

  personName: string
  // Resolves `person_name` or a fallback for display.

  amount?: number
  // Minor units for `MoneyCell`.

  date: string
  // Localized calendar date string (no time of day), computed via
  // `formatDateTime`; groups rows under sticky date headers and becomes
  // `PaymentDateHeaderRow.date`. Not rendered per-row on either desktop or
  // mobile — the header shows it once per group.

  time: string
  // Localized time-of-day string for desktop's `Time` column and mobile's
  // right cell — the date itself is shown once in the sticky header
  // instead of repeating per row.

  paymentStatus: string
  // `payment_status` for display.

  text: string | null
  // Raw payment message text from the API; shown on mobile under the status chip.

  subscription: string | null
  // Name of the subscription bought with this payment, from the catalog (generic
  // label without `subscription_types_view`); null when the payment has none.

  _raw: Payment
  // Full API record; used by the row edit handler to pre-fill the form.
}

interface PaymentDateHeaderRow {
  // Synthetic full-width divider row spliced into `groupedData` ahead of
  // each date's first payment; rendered by the shared `DateHeaderRowCell`
  // via `pickRowOverride`. Shared by both desktop and mobile.

  kind: 'date-header'
  id: string
  date: string
  count: number
  unitKey: string
}

type PaymentListItem = PaymentRow | PaymentDateHeaderRow

/**
 * Icon map for `payment_status` (mirrors the backend `PaymentStatus` enum):
 *
 * - `IDENTIFIED`   → green check (the only fully resolved state).
 * - `AMBIGUOUS`    → amber warning triangle ("?" inside) — needs review.
 * - `UNIDENTIFIED` → amber warning triangle — same review treatment as ambiguous.
 * - `NONE`         → red X (no status assigned).
 *
 * Title strings are used both as the tooltip and the `aria-label` so the icon
 * remains meaningful for assistive tech and on hover. A computed (not a
 * plain const) so the titles re-translate when the user switches language.
 */
const paymentStatusIcons = computed<Record<string, EnumCellIconSpec>>(() => ({
  IDENTIFIED: {
    icon: 'check',
    color: 'var(--color-money-positive)',
    title: t('tabs.payments.statusIdentified'),
  },
  AMBIGUOUS: {
    icon: 'warning-triangle',
    color: 'var(--color-visit-stale)',
    title: t('tabs.payments.statusAmbiguous'),
  },
  UNIDENTIFIED: {
    icon: 'warning-triangle',
    color: 'var(--color-visit-stale)',
    title: t('tabs.payments.statusUnidentified'),
  },
  NONE: {
    icon: 'x',
    color: 'var(--color-money-negative)',
    title: t('tabs.payments.statusNone'),
  },
}))

/**
 * Resolves a label for the person column when the API does not return a name.
 */
function personDisplayName(name: string | null | undefined, personId: string): string {
  if (name != null && name.trim() !== '') {
    return formatName(name)
  }
  return t('common.personFallback', { id: personId })
}

// Column widths sum to ~48rem (≈ `--width-middle-column` minus the
// article's 4rem horizontal padding) so this table has the same total
// width as every other tab's table; see `App.vue` for context.
//
// Typed over `PaymentListItem` (not just `PaymentRow`) and each props()
// casts its row, because `groupedData` — fed to both desktop and mobile —
// also contains synthetic `date-header` rows; `rowOverride` guarantees
// props() is never actually invoked for those (mirrors `mobileColumns`
// below). Computed (not a plain const) so labels re-translate when the
// user switches language.
const columns = computed<Column<PaymentListItem>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: (row as PaymentRow).rowNumber }),
    width: '2.5rem',
    align: 'center',
  },
  {
    key: 'paymentStatus',
    label: '',
    component: markRaw(EnumCell),
    props: (row) => {
      const r = row as PaymentRow
      return {
        value: r.paymentStatus,
        iconMap: paymentStatusIcons.value,
        defaultIcon: {
          icon: 'warning-triangle',
          color: 'var(--color-visit-stale)',
          title: r.paymentStatus,
        } satisfies EnumCellIconSpec,
      }
    },
    width: '2.5rem',
    align: 'center',
  },
  {
    key: 'time',
    // Date is shown once per group in the sticky `DateHeaderRowCell`
    // divider (see `groupedData`), so this column only needs the time.
    label: t('common.time'),
    component: markRaw(TextCell),
    props: (row) => ({ value: (row as PaymentRow).time }),
    width: '4.5rem',
  },
  {
    key: 'personName',
    label: t('common.client'),
    component: markRaw(NameCell),
    props: (row) => ({ name: (row as PaymentRow).personName }),
    width: '16rem',
  },
  {
    key: 'subscription',
    label: t('tabs.payments.colSubscription'),
    component: markRaw(SubscriptionTagCell),
    props: (row) => ({ name: (row as PaymentRow).subscription }),
    width: '14rem',
  },
  {
    key: 'amount',
    label: moneyColumnLabel(t('common.amount')),
    component: markRaw(MoneyCell),
    props: (row) => ({ amount: (row as PaymentRow).amount, emptyPlaceholder: '–' }),
    width: '8.5rem',
    align: 'left',
  },
])

const mobileColumns = computed<Column<PaymentListItem>[]>(() => [
  {
    key: 'personName',
    label: '',
    component: markRaw(PaymentMobileLeftCell),
    // Never invoked for `date-header` rows: `rowOverride` makes DynamicTable
    // skip the per-column props() pipeline entirely for those rows.
    props: (row) => {
      const r = row as PaymentRow
      return {
        name: r.personName,
        paymentStatus: r.paymentStatus,
        text: r.text,
        subscription: r.subscription,
      }
    },
  },
  {
    key: 'amount',
    label: '',
    component: markRaw(PaymentMobileRightCell),
    props: (row) => {
      const r = row as PaymentRow
      return { amount: r.amount, time: r.time }
    },
    width: '7rem',
    align: 'right',
  },
])

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['payments'],
  queryFn: ({ pageParam }) => fetchPayments(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) => (lastPage.has_next_page ? lastPage.next_cursor : undefined),
  refetchInterval: paymentsPollingInterval,
  refetchIntervalInBackground: false,
})

const subscriptionTypeNames = computed(
  () => new Map(subscriptionTypes.value.map((type) => [type.id, type.name])),
)

const data = computed<PaymentRow[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  let index = 0
  return pages.flatMap((page) =>
    (page.items ?? []).map((p) => {
      index += 1
      const { formattedDate, formattedTime } = formatDateTime({ date: p.date })
      return {
        kind: 'payment',
        id: p.id,
        rowNumber: index,
        personName: personDisplayName(p.person_name, p.person_id),
        amount: paymentAmountMinorUnits(p),
        date: formattedDate,
        time: formattedTime,
        paymentStatus: p.payment_status,
        text: p.text,
        subscription: p.subscription_type_id
          ? (subscriptionTypeNames.value.get(p.subscription_type_id) ??
            t('cards.payment.subscriptionFallback'))
          : null,
        _raw: p,
      }
    }),
  )
})

/**
 * Shared row list for both desktop and mobile: `data` with a synthetic
 * `date-header` row spliced in ahead of each date's first payment. Rows are
 * already grouped contiguously by date (the API returns them in that
 * order), so a single pass comparing against the previous row's date is
 * enough — mirrors `ServicesTab`'s `groupedData`.
 */
const groupedData = computed<PaymentListItem[]>(() => {
  const result: PaymentListItem[] = []
  let currentHeader: PaymentDateHeaderRow | null = null
  for (const row of data.value) {
    if (!currentHeader || currentHeader.date !== row.date) {
      currentHeader = {
        kind: 'date-header',
        id: `date-header:${row.date}`,
        date: row.date,
        count: 0,
        unitKey: 'units.payment',
      }
      result.push(currentHeader)
    }
    currentHeader.count += 1
    result.push(row)
  }
  return result
})

/** Selects `DateHeaderRowCell` for synthetic date-header rows so the table
 * renders one full-width sticky divider instead of the normal row cells.
 * Returns `null` for payment rows on both desktop and mobile. */
function pickRowOverride(row: unknown): Component | null {
  if ((row as PaymentListItem).kind === 'date-header') return markRaw(DateHeaderRowCell)
  return null
}

function rowKeyFn(row: unknown): string {
  return (row as PaymentListItem).id
}

function onRowClick(row: unknown) {
  clearInfoViewSelections('payment')
  paymentCardStore.selectPayment((row as PaymentRow).id)
}

const selectedRowKey = computed<string | null>(() => paymentCardStore.selectedPaymentId)

const headerRef = ref<HTMLElement | null>(null)
const isHeaderVisible = useIsVisible(headerRef)

function onAddPayment(): void {
  clearInfoViewSelections('addPaymentForm')
  addPaymentFormStore.open()
}

const queryClient = useQueryClient()
const deleteMutation = useMutation({ mutationFn: (id: string) => deletePayment(id) })

function onRowDeleteHandler(row: unknown): void {
  const r = row as PaymentRow
  if (!confirm(t('tabs.payments.confirmDelete'))) return
  deleteMutation.mutate(r.id, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      if (paymentCardStore.selectedPaymentId === r.id) paymentCardStore.clearSelection()
    },
  })
}

function onRowEditHandler(row: unknown): void {
  const p = (row as PaymentRow)._raw
  clearInfoViewSelections('addPaymentForm')
  addPaymentFormStore.openForEdit(p.id, {
    personId: p.person_id,
    personName: p.person_name ?? null,
    amount: p.amount,
    date: p.date,
    link: p.link ?? null,
    transactionId: p.transaction_id ?? null,
    text: p.text ?? null,
    billId: p.bill_id ?? null,
    paymentStatus: p.payment_status,
    subscriptionTypeId: p.subscription_type_id ?? null,
  })
}
</script>

<template>
  <article>
    <div v-if="isMobile" ref="mobileScrollSentinel" class="scroll-sentinel" aria-hidden="true" />
    <div
      ref="headerRef"
      class="tab-header"
      :class="{
        'tab-header--mobile': isMobile,
        'tab-header--mobile-scrolled': isMobileScrolled,
      }"
    >
      <h1 v-if="!isMobileScrolled">{{ t('tabs.payments.title') }}</h1>
      <AddButton
        v-if="!isMobile && hasRole(ResourceAccessRole.PaymentsCreate)"
        :label="t('tabs.payments.addPayment')"
        @click="onAddPayment"
      />
      <CompactAddButton
        v-if="isMobile && hasRole(ResourceAccessRole.PaymentsCreate)"
        :label="t('tabs.payments.addPayment')"
        @click="onAddPayment"
      />
    </div>
    <p v-if="isPending" class="text-tufte-muted">{{ t('tabs.payments.loading') }}</p>
    <p v-else-if="isError" class="text-red-600">
      {{
        t('tabs.payments.loadError', {
          message: (error as Error)?.message ?? t('common.unknownError'),
        })
      }}
    </p>
    <template v-else>
      <!-- pinnable-header is always false: sticky date-header rows and a
           pinned <thead> both target `top: 0` on the same scroll container
           and would visually collide; `ClientCard`'s visits table hit this
           same conflict and resolved it the same way. -->
      <DynamicTable
        :class="isMobile ? 'payments-table--mobile' : 'payments-table'"
        :columns="(isMobile ? mobileColumns : columns) as Column<unknown>[]"
        :data="groupedData"
        :has-next-page="hasNextPage"
        :is-fetching-next-page="isFetchingNextPage"
        :on-load-more="fetchNextPage"
        :on-row-click="onRowClick"
        :row-override="pickRowOverride"
        :row-key="rowKeyFn"
        :selected-row-key="isMobile ? null : selectedRowKey"
        :on-row-edit="
          isMobile || !hasRole(ResourceAccessRole.PaymentsEdit) ? undefined : onRowEditHandler
        "
        :on-row-delete="
          isMobile || !hasRole(ResourceAccessRole.PaymentsDelete) ? undefined : onRowDeleteHandler
        "
        :on-add="isMobile || !hasRole(ResourceAccessRole.PaymentsCreate) ? undefined : onAddPayment"
        :pinnable-header="false"
        :compact-add-visible="!isMobile && !isHeaderVisible"
        storage-key="payments"
      />
    </template>
    <PaymentCardMobileScreen
      v-if="isMobile && paymentCardStore.hasSelection"
      :payment-id="paymentCardStore.selectedPaymentId!"
      @close="paymentCardStore.clearSelection()"
    />
  </article>
</template>

<style scoped>
/*
 * The table stretches to fill the middle column (`width: 100%` default) so
 * that any extra horizontal space the three-column layout gives the middle
 * column — e.g. when the sidebar is collapsed — is reflected in the table
 * itself instead of leaving a gap to the right of it.
 */

.tab-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.scroll-sentinel {
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.tab-header--mobile {
  padding: 0.75rem 1.25rem;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
}

.tab-header--mobile h1 {
  margin-top: 0;
  margin-bottom: 0;
  font-size: var(--text-tufte-mobile-title);
}

.tab-header--mobile-scrolled {
  padding: 0.4rem 1.25rem;
  justify-content: flex-end;
}

/* Mobile table: full-width, no thead, tighter horizontal padding. */
.payments-table--mobile :deep(.dynamic-table) {
  width: 100%;
}

.payments-table--mobile :deep(thead) {
  display: none;
}

.payments-table--mobile :deep(td) {
  padding: 0.7rem 1.25rem;
}

/* `MoneyCell` defaults to center; this table uses left-aligned amounts. */
.payments-table :deep(.money-cell) {
  text-align: left;
}
</style>
